import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Tabs,
  Tab,
  Paper,
  Box,
  Typography,
  Fade,
  Chip,
  Card,
  CardContent,
} from '@material-ui/core';
import {
  Dashboard,
  School,
  Assessment,
  Person,
} from '@material-ui/icons';
import { useQuery } from 'react-query';
import usePortalAuth from '../../../auth/usePortalAuth';
import basePCUrl from '../../../Globals';
import { fetchApiData } from '../../../utils/service';

// Import existing components
import TrainingDashboardSwitch from './TrainingDashboardSwitch';
import UserTrainingInterface from './UserTrainingInterface';
import TeamTrainingReport from './TeamTrainingReport';
import MyTrainingReport from './MyTrainingReport';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  },
  uberTabsContainer: {
    position: 'sticky',
    top: 100,
    // zIndex: 15,
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(15px)',
    borderBottom: '2px solid rgba(0, 85, 162, 0.1)',
    boxShadow: '0 8px 32px rgba(0, 85, 162, 0.15)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(0, 85, 162, 0.03) 0%, rgba(66, 139, 201, 0.02) 100%)',
      pointerEvents: 'none',
    },
  },
  uberTabsWrapper: {
    maxWidth: '100%',
    padding: theme.spacing(2, 3),
    position: 'relative',
    zIndex: 1,
  },
  tabsIndicator: {
    background: 'linear-gradient(45deg, #0055A2, #428BC9)',
    height: 3,
    borderRadius: '2px 2px 0 0',
  },
  uberTab: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    minWidth: 160,
    padding: theme.spacing(1.5, 3),
    color: '#666',
    '&.Mui-selected': {
      color: '#0055A2',
      fontWeight: 700,
    },
    '&:hover': {
      color: '#0055A2',
      background: 'rgba(0, 85, 162, 0.05)',
    },
  },
  newBadge: {
    marginLeft: 8,
    background: 'linear-gradient(45deg, #ff6b6b, #ee5a52)',
    color: 'white',
    fontSize: '0.7rem',
    height: 18,
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '50%': {
      transform: 'scale(1.1)',
      opacity: 0.8,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
  contentContainer: {
    minHeight: 'calc(100vh - 80px)',
    position: 'relative',
  },
  tabContent: {
    width: '100%',
    minHeight: '100%',
  },
}));


const MinimalTrainingUberTabs = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { getAccessTokenSilently } = usePortalAuth();
  const getTranslation = props.getTranslation || ((value) => value);
  const preferredLanguage = props.preferredLanguage;

  const selectedClientId = props.selectedClient?.clientId ?? props.selectedClient;
  const isAdmin = props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin';

  const hideTabBar = props.defaultTab === 'user-training';
  // When hideTabBar is true we want to land on User Training.
  // Its index shifts by 1 for admins because Command Center is first.
  const defaultIndex = hideTabBar ? (isAdmin ? 1 : 0) : 0;
  const [currentTab, setCurrentTab] = useState(defaultIndex);

  // Fetch team training report to determine if the current user has any direct
  // report data. Reuses the same query key as TeamTrainingReport so the
  // result is shared via react-query's cache.
  const { data: teamReportData } = useQuery(
    ['team-training-report', selectedClientId],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchApiData(basePCUrl, token, 'getTeamTrainingReport', props.accessLevel, selectedClientId);
    },
    {
      enabled: !!selectedClientId,
      staleTime: 1000 * 60 * 15, // 15 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
    }
  );

  const hasDirectReports = !!(
    teamReportData?.trainingReport?.managers?.[0]?.directReports?.length
  );
  const shouldShowTeamReport = isAdmin || hasDirectReports;

  // Tab configuration. Command Center is admin-only. Team Report is only
  // included when the user has direct reports or is an admin.
  const tabs = useMemo(() => {
    const base = [];

    if (isAdmin) {
      base.push({
        id: 'command-center',
        label: getTranslation('Command Center', preferredLanguage),
        icon: <Dashboard fontSize="small" />,
        description: getTranslation(
          'Executive dashboard with analytics and management tools',
          preferredLanguage
        ),
        component: 'existing'
      });
    }

    base.push({
      id: 'user-training',
      label: getTranslation('User Training', preferredLanguage),
      icon: <School fontSize="small" />,
      description: getTranslation(
        'Training modules and progress tracking for users',
        preferredLanguage
      ),
      component: 'user-training'
    });

    if (shouldShowTeamReport) {
      base.push({
        id: 'team-report',
        label: getTranslation('Team Report', preferredLanguage),
        icon: <Assessment fontSize="small" />,
        description: getTranslation(
          'Team training compliance and progress report',
          preferredLanguage
        ),
        component: 'team-report'
      });
    }

    base.push({
      id: 'my-report',
      label: getTranslation('My Report', preferredLanguage),
      icon: <Person fontSize="small" />,
      description: getTranslation('Your personal training compliance report', preferredLanguage),
      component: 'my-report'
    });

    return base;
  }, [getTranslation, isAdmin, preferredLanguage, shouldShowTeamReport]);

  // Set initial tab based on URL parameter or the defaultTab prop. We resolve
  // tab indices by id so the routing still works when tabs are conditionally
  // hidden (e.g. Team Report not shown for users with no direct reports).
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('view');

    const indexById = (id) => tabs.findIndex((t) => t.id === id);

    let nextIndex = -1;
    if (tabParam === 'user' || tabParam === 'user-training') {
      nextIndex = indexById('user-training');
    } else if (tabParam === 'command-center') {
      nextIndex = indexById('command-center');
    } else if (tabParam === 'team-report') {
      nextIndex = indexById('team-report');
      // Fall back to My Report if Team Report is hidden.
      if (nextIndex === -1) nextIndex = indexById('my-report');
    } else if (tabParam === 'my-report') {
      nextIndex = indexById('my-report');
    }

    if (nextIndex !== -1) {
      setCurrentTab(nextIndex);
    }
  }, [location, tabs]);

  // If the currently selected tab no longer exists (e.g. team-report was
  // removed because the user has no direct reports), fall back to a sane tab.
  useEffect(() => {
    if (currentTab >= tabs.length) {
      setCurrentTab(hideTabBar ? Math.min(1, tabs.length - 1) : 0);
    }
  }, [currentTab, tabs.length, hideTabBar]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    
    // Update URL with tab parameter
    const currentTabConfig = tabs[newValue];
    const urlParams = new URLSearchParams(location.search);
    
    if (currentTabConfig.id === 'user-training') {
      urlParams.set('view', 'user');
    } else if (currentTabConfig.id === 'team-report') {
      urlParams.set('view', 'team-report');
    } else if (currentTabConfig.id === 'my-report') {
      urlParams.set('view', 'my-report');
    } else {
      urlParams.delete('view');
    }
    
    const newSearch = urlParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    // Use replace to avoid cluttering browser history
    history.replace(newUrl);
  };

  const renderTabContent = () => {
    const selectedTab = tabs[currentTab];
    
    if (selectedTab.component === 'existing') {
      return (
        <div style={{ background: 'transparent' }}>
          <TrainingDashboardSwitch
            isLoading={props.isLoading}
            accessLevel={props.accessLevel}
            selectedClient={props.selectedClient}
            defaultClient={props.defaultClient}
            history={props.history}
            location={props.location}
            getTranslation={props.getTranslation}
            preferredLanguage={props.preferredLanguage}
            trainingData={props.trainingData}
            adminTrainingData={props.adminTrainingData}
            userClickOverviewData={props.userClickOverviewData}
          />
        </div>
      );
    } else if (selectedTab.component === 'my-report') {
      return (
        <MyTrainingReport
          accessLevel={props.accessLevel}
          selectedClient={props.selectedClient}
          getTranslation={props.getTranslation}
          preferredLanguage={props.preferredLanguage}
          trainingData={props.trainingData}
        />
      );
    } else if (selectedTab.component === 'team-report') {
      return (
        <TeamTrainingReport
          accessLevel={props.accessLevel}
          selectedClient={props.selectedClient}
          history={props.history}
          location={props.location}
          getTranslation={props.getTranslation}
          preferredLanguage={props.preferredLanguage}
          trainingData={props.trainingData}
          adminTrainingData={props.adminTrainingData}
          usernameList={props.usernameList}
        />

      );
    } else {
      return (
        <UserTrainingInterface
          accessLevel={props.accessLevel}
          selectedClient={props.selectedClient}
          getTranslation={props.getTranslation}
          preferredLanguage={props.preferredLanguage}
          trainingData={props.trainingData}
        />
      );
    }
  };

  return (
    <div className={`${classes.root} ${currentTab === 0 ? 'existing-view' : 'modern-view'}`}>
      {/* Uber Tabs - hidden when accessed from TrainingDashboard route */}
      {!hideTabBar && (
        <Paper className={classes.uberTabsContainer} elevation={0}>
          <Box className={classes.uberTabsWrapper}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                classes={{
                  indicator: classes.tabsIndicator,
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={tab.id}
                    label={
                      <Box display="flex" alignItems="center">
                        {tab.icon}
                        <Box ml={1}>
                          {tab.label}
                        </Box>
                        {tab.isNew && (
                          <Chip
                            label={getTranslation('NEW', preferredLanguage)}
                            size="small"
                            className={classes.newBadge}
                          />
                        )}
                      </Box>
                    }
                    className={classes.uberTab}
                  />
                ))}
              </Tabs>
              
              {/* Tab Description */}
              <Box flex={1} ml={3}>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  style={{ fontSize: '0.875rem' }}
                >
                  {tabs[currentTab]?.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Tab Content */}
      <div className={classes.contentContainer}>
        <Fade in={true} timeout={500}>
          <div className={classes.tabContent}>
            {renderTabContent()}
          </div>
        </Fade>
      </div>
    </div>
  );
};

export default MinimalTrainingUberTabs;